<template>
  <div data-dynatrace="product-price" data-test-id="product-pricing">
    <span
      class="gap-x-1 i-flex wrap"
      :class="[classWrapper, { 'f-col gap-y-0.5': block }]"
    >
      <template v-if="isRange && !showDiscountedRanges">
        <span :class="classCurrent">{{ current }}</span>
      </template>
      <template v-else>
        <template v-if="$feature.showDynamicPricing">
          <template v-if="isDynamicPrice">
            <ins :class="[classCurrent, brandClasses.sale]" style="text-decoration: none">
              <span v-if="showDynamicPricingOnPlp" class="fw-normal">
                <del v-style:c="originalPriceColor" :class="brandClasses.original">
                  <span class="sr-only">{{ $t.originalPrice }}:</span>
                  {{ original }}
                </del>
                <span class="ml-1">
                  {{ $t.from }} {{ current }} - {{ replaceAll($t.uptoOff, { percentage: discountPercentage }) }}
                </span>
              </span>
              <span v-else class="fw-medium">
                <span :class="{ '<lg:hidden': !showOmnibusDetails }">{{ $t.from }}</span>
                <span class="ml-1 fw-normal" :class="classCurrent">{{ current }}</span>
                <del v-style:c="originalPriceColor" class="ml-1" :class="brandClasses.original">
                  <span class="sr-only">{{ $t.originalPrice }}:</span>
                  {{ original }}
                </del>
                <br>
                <span
                  v-if="showDiscountPercentage"
                  class="text-xs"
                  :class="{ '<lg:hidden': !showOmnibusDetails }"
                >
                  {{ replaceAll($t.uptoOff, { percentage: discountPercentage }) }}
                </span>
              </span>
            </ins>
          </template>
          <template v-else>
            <span v-if="!isVisuallyDiscounted" class="fw-normal" :class="classCurrent">{{ current }}</span>
            <ins v-else :class="[brandClasses.sale]" style="text-decoration: none">
              <template v-if="showDynamicPricingOnPlp">
                <del v-style:c="originalPriceColor" class="mr-1" :class="brandClasses.original">
                  <span class="sr-only">{{ $t.originalPrice }}:</span>
                  {{ isOmnibus ? historicBest : original }}
                </del>
                <span class="sr-only">{{ $t.currentDiscountedPrice }}:</span>
                <span :class="classCurrent">
                  {{ current }} ({{ isOmnibus ? historicDiscountPercentage : discountPercentage }})
                </span>
              </template>
              <template v-else>
                <del v-if="(!isOmnibus || compactOmnibus) && !suppressSaleStyling" v-style:c="originalPriceColor" :class="brandClasses.original">
                  <span class="sr-only">{{ $t.originalPrice }}:</span>
                  {{ original }}
                </del>
                <component :is="block ? 'div' : 'span'" class="ws-nowrap">
                  <span class="sr-only">{{ $t.currentDiscountedPrice }}:</span>
                  <span :class="classCurrent">{{ current }}
                    <template v-if="showDiscountPercentage && !isRange">
                      ({{ discountPercentage }})
                    </template>
                  </span>
                </component>
              </template>
            </ins>
          </template>
        </template>
        <template v-else>
          <component
            :is="!isVisuallyDiscounted ? 'span' : 'del'"
            v-if="(!isOmnibus || !showOmnibusDetails || compactOmnibus)
              && !hideStrikethroughPrice && !suppressSaleStyling"
            v-style:c="originalPriceColor"
            :class="!isVisuallyDiscounted ? classCurrent : brandClasses.original"
          >
            <span v-if="isDiscounted" class="sr-only">
              {{ isOmnibus ? $t.previousDiscountedPrice : $t.originalPrice }}:
            </span>
            {{ isOmnibus ? historicBest : original }}
          </component>
          <del v-if="price.previousBestSalesPrice && !suppressSaleStyling" :class="!isVisuallyDiscounted ? classCurrent : brandClasses.original">
            <span class="sr-only">{{ $t.previousDiscountedPrice }}:</span>
            {{ useFormattedPrice(price.previousBestSalesPrice, currency) }}
          </del>
          <ins v-if="isVisuallyDiscounted" v-style:c="salePriceColor" :class="[classCurrent, brandClasses.sale]" style="text-decoration: none">
            <span class="sr-only">
              {{ price.previousBestSalesPrice || isOmnibus ? $t.currentDiscountedPrice : $t.discountedPrice }}:
            </span>
            {{ current }}
            <template v-if="showDiscountPercentage && !isRange && (!showOmnibusDetails || compactOmnibus)">
              ({{ isOmnibus ? historicDiscountPercentage : discountPercentage }})
            </template>
          </ins>
          <span v-if="suppressSaleStyling" class="fw-normal" :class="classCurrent">{{ current }}</span>
        </template>
      </template>
      <vf-tooltip
        v-if="showSalePriceTooltip && price.salesPriceActiveDate"
        v-style:c="suppressSaleStyling ? undefined : salePriceColor"
        class="-ml-1"
        :class="suppressSaleStyling ? '' : brandClasses.sale"
      >
        <template #trigger>
          <vf-icon name="info" />
        </template>
        {{
          replaceAll(
            $t.salesPriceActiveDateInfo,
            { date: formatDate(new Date(price.salesPriceActiveDate), 'MMMM YYYY') },
          )
        }}
      </vf-tooltip>
    </span>
    <transition name="slide">
      <div v-if="isOmnibus" :class="{ '<lg:hidden': !showOmnibusDetails }">
        <div v-if="!compactOmnibus" class="mt-1 w-fit flex gap-x-1 text-xs fw-medium">
          <span class="fw-normal">{{ $t.lowestRecentPrice }}:</span> <del>{{ historicBest }}</del>
          <span v-if="showDiscountPercentage" class="c-brand-3">
            {{ historicDiscountPercentage }}
          </span>
        </div>
        <div v-if="showOriginalPriceForOmnibus" class="mt-1 w-fit flex gap-x-1 text-xs fw-medium">
          <span class="fw-normal">{{ $t.originalPrice }}:</span> <del>{{ original }}</del>
          <span v-if="showDiscountPercentage" class="c-brand-3">
            {{ discountPercentage }}
          </span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import type { RecommendationStyle } from '#content/contexts/recommendation'
import type { CSSClass } from '#types/common'
import type { PriceFull } from '#types/product'
import { RecommendationStyleContextKey } from '#content/contexts/recommendation'
import { formatDate } from '@vueuse/core'

const {
  currency,
  isDigitalProduct,
  price,
  showOmnibusDetails = true
} = defineProps<{
  /**
   * Price to display
   * Example of strike through sales price range and current price range.
   *
   * $20-$40 - Original Price range: - strike through
   * $10-$30 - Current Range.
   * $20 - lowOriginal
   * $40 - highOriginal
   * $10 - lowCurrent
   * $30 - highCurrent
   */
  price: PriceFull
  /**
   * Currency of the price
   */
  currency: string
  /**
   * Classes to be applied to the current price
   */
  classCurrent?: CSSClass
  /**
   * Classes to be applied to the wrapper element
   */
  classWrapper?: CSSClass
  /**
   * Show or hide the omnibus details, use for drawer interaction
   * Default: true
   */
  showOmnibusDetails?: boolean
  /**
   * Sets displaying the discount amount as percentage
   */
  showDiscountPercentage?: boolean
  /**
   * Shows box with original price and omnibus tooltip below sale price.
   * Currently only shown on PDP and Quickshop
   */
  showOriginalPriceForOmnibus?: boolean
  /**
   * Show original price and discounted price below it
   * Currently only use on order Details
   */
  block?: boolean
  compactOmnibus?: boolean
  isDigitalProduct?: boolean
  /**
   * Show dynamic pricing on PLP
   */
  showDynamicPricingOnPlp?: boolean
  showSalePriceTooltip?: boolean
  hideStrikethroughPrice?: boolean
}>()

const { brandClasses, showDiscountedRanges } = useAppConfig().components.product.pricing
const { $feature, $t } = useNuxtApp()

const formatDiscount = (percentage: number) =>
  `${$feature.showNegativeDiscountPercentageOnCatalog ? '-' : ''}${percentage}%`

const formatPrice = (low: number, high: number) => {
  if (($feature.showDynamicPricing && !isDigitalProduct) || low === high)
    return low === 0 ? $t.free : useFormattedPrice(low, currency)
  return `${useFormattedPrice(low, currency)} - ${useFormattedPrice(high, currency)}`
}

const original = computed(() => formatPrice(price.lowOriginal, price.highOriginal))

const current = computed(() => formatPrice(price.lowCurrent, price.highCurrent))

const historicBest = computed(() => useFormattedPrice(price.historicBestPrice ?? 0, currency))

const isDiscounted = computed(() => current.value !== original.value)

const isOmnibus = computed(() => {
  const { historicBestPrice, lowCurrent, lowOriginal } = price
  return (
    $feature.showOmnibusPricing
    && historicBestPrice !== undefined
    && lowCurrent < lowOriginal
    && lowCurrent < historicBestPrice
  )
})

const isRange = computed(() => price.lowCurrent !== price.highCurrent)

const isDynamicPrice = computed(() => !price.historicBestPrice && isRange.value && isDiscounted.value)

const discountPercentage = computed(() => {
  const { highOriginal, lowCurrent } = price
  const percentage = getPercentage(highOriginal, highOriginal - lowCurrent, 0)
  return formatDiscount(percentage)
})

const historicDiscountPercentage = computed(() => {
  const { percentagePriceDiscount } = price
  return percentagePriceDiscount ? formatDiscount(percentagePriceDiscount) : ''
})

// If Omnibus info is available but current price is not below the 30-day lowest,
// suppress sale/red styling and treat price as non-discounted for display.
const suppressSaleStyling = computed(
  () => $feature.showOmnibusPricing && price.historicBestPrice && price.lowCurrent >= price.historicBestPrice
)

const isVisuallyDiscounted = computed(() => isDiscounted.value && !suppressSaleStyling.value)

const { originalPriceColor, salePriceColor } = inject(RecommendationStyleContextKey, {} as RecommendationStyle)
</script>
